import axios from 'axios';
import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import Offcanvas from 'react-bootstrap/Offcanvas';
import { adminMainUrl } from '../../../../Config';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  getCompanyBranch,
  getJobOrderPortList,
} from '../../../../Redux/action/Joborder/JoborderAction';
import { getIncoterms } from '../../../../Redux/action/MasterAction';
import { getFFAirExpOrganisation } from '../../../../Redux/action/FreightForwarding/Console/FFAirExpAction';
import { customStyles } from '../../../../HelperFunctions';

const JobOrderEditOffcanvas = ({
  token,
  user_id,
  show,
  b64id,
  handleClose,
  loadingUnusedQuotationList,
  UnusedQuotationListError,
  UnusedQuotationList,
  loadingJobOrderEmployeeList,
  JobOrderEmployeeListError,
  JobOrderEmployeeList,
  JobOrderDetail,
  refresh,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [disableSubmit, setDisableSubmit] = useState(false);

  // ---- Dropdown options ----
  const [quotationOption, setQuotationOptions] = useState([]);
  const [loadingQuotation, setLoadingQuotation] = useState(false);
  const [branchOption, setBranchOption] = useState([]);
  const [loadingBranch, setLoadingBranch] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [portOptions, setPortOptions] = useState([]);
  const [incotermOptions, setIncotermOptions] = useState([]);
  const [consigneeOptions, setConsigneeOptions] = useState([]);
  const [shipperOptions, setShipperOptions] = useState([]);
  const [isDirectCustomer, setIsDirectCustomer] = useState(false); // from JobOrderDetail

  // ---- Static options ----
  const loadTypeOptions = [
    { value: 'FCL', label: 'FCL (Full Container Load)' },
    { value: 'LCL', label: 'LCL (Less than Container Load)' },
  ];

  const shipmentTypeOptions = [
    { value: '20DRY', label: "20' Dry Standard" },
    { value: '40DRY', label: "40' Dry Standard" },
    { value: '40DRYHC', label: "40' Dry High Cube" },
    { value: '45DRYHC', label: "45' Dry High Cube" },
    { value: '20TANK', label: "20' Tank" },
    { value: '40TANK', label: "40' Tank" },
    { value: '20REEF', label: "20' Reefer Standard" },
    { value: '40REEFHC', label: "40' Reefer High Cube" },
    { value: '40REEF', label: "40' Reefer Standard" },
    { value: '20OT', label: "20' Open Top" },
    { value: '40OT', label: "40' Open Top" },
    { value: '40OTHC', label: "40' Open Top High" },
    { value: '20FR', label: "20' Flat Rack" },
    { value: '40FR', label: "40' Flat Rack Standard" },
    { value: '40FRHC', label: "40' Flat Rack High" },
    { value: 'BULK', label: 'Bulk' },
    { value: 'BREAKBULK', label: 'Breakbulk' },
    { value: 'OTHER', label: 'Other' },
  ];

  const weightUnitOptions = [
    { value: 'Kg', label: 'Kg' },
    { value: 'Ton', label: 'Ton' },
  ];

  const dimensionUnitOptions = [
    { value: 'cm', label: 'cm' },
    { value: 'inches', label: 'inches' },
    { value: 'm', label: 'm' },
    { value: 'ft', label: 'ft' },
  ];

  // ---- Redux states ----
  const { JobOrderPortList, loadingJobOrderPortList } = useSelector(
    (state) => state?.JoborderReducer || {}
  );
  const { incotermsData, loadingIncoterms } = useSelector(
    (state) => state?.MasterReducer || {}
  );
  const { FFAirExpOrganisationList, loadingFFAirExpOrganisationList } = useSelector(
    (state) => state?.FFAirExpReducer || {}
  );
  const { JobOrderCompanyBranchList, loadingJobOrderCompanyBranchList } = useSelector(
    (state) => state?.JoborderReducer || {}
  );

  // ---- Fetch master data ----
  useEffect(() => {
    dispatch(getJobOrderPortList());
    dispatch(getIncoterms());
    dispatch(getFFAirExpOrganisation(token, user_id));
  }, []);

  // ---- Populate port options ----
  useEffect(() => {
    if (JobOrderPortList && Array.isArray(JobOrderPortList)) {
      const arr = JobOrderPortList.map((v) => ({ value: v.id, label: v.name }));
      setPortOptions(arr);
    } else {
      setPortOptions([]);
    }
  }, [JobOrderPortList]);

  // ---- Populate incoterm options ----
  useEffect(() => {
    if (incotermsData && Array.isArray(incotermsData)) {
      const arr = incotermsData.map((item) => ({
        value: item.id,
        label: item.name,
      }));
      setIncotermOptions(arr);
    } else {
      setIncotermOptions([]);
    }
  }, [incotermsData]);

  // ---- Build consignee & shipper options ----
  const [organisationOptions, setOrganisationOptions] = useState([]);
  useEffect(() => {
    if (FFAirExpOrganisationList) {
      const arr = FFAirExpOrganisationList.map((val) => ({
        value: val.id,
        label: `${val.name} (${val.customerType})`,
        entity: val.entity_type ? JSON.parse(val.entity_type) : [],
      }));
      setOrganisationOptions(arr);
    }
  }, [FFAirExpOrganisationList]);

  useEffect(() => {
    if (organisationOptions.length) {
      const shipperArr = organisationOptions.filter((obj) =>
        obj.entity && obj.entity.includes('shipper')
      );
      const consigneeArr = organisationOptions.filter((obj) =>
        obj.entity && obj.entity.includes('consignee')
      );
      setShipperOptions(shipperArr);
      setConsigneeOptions(consigneeArr);
    }
  }, [organisationOptions]);

  // ---- Employee options ----
  useEffect(() => {
    if (loadingJobOrderEmployeeList) {
      setLoadingEmployee(true);
    } else {
      setLoadingEmployee(false);
    }
    if (JobOrderEmployeeList) {
      const arr = JobOrderEmployeeList.map((val) => ({
        value: val.user_id,
        label: val.name,
      }));
      setEmployeeOptions(arr);
    }
  }, [loadingJobOrderEmployeeList, JobOrderEmployeeListError, JobOrderEmployeeList]);

  // ---- Quotation options ----
  useEffect(() => {
    if (loadingUnusedQuotationList) {
      setLoadingQuotation(true);
    } else {
      setLoadingQuotation(false);
    }
    if (UnusedQuotationList) {
      const arr = UnusedQuotationList.map((val) => ({
        value: val.id,
        label: val.ship_quotation_no,
      }));
      setQuotationOptions(arr);
    }
  }, [loadingUnusedQuotationList, UnusedQuotationListError, UnusedQuotationList]);

  // ---- Branch options ----
  useEffect(() => {
    if (JobOrderCompanyBranchList) {
      const arr = JobOrderCompanyBranchList.map((v) => ({
        value: v.id,
        label: v.branch_name,
      }));
      setBranchOption(arr);
    }
  }, [JobOrderCompanyBranchList]);

  // ---- Formik initial values ----
  const initialValues = {
    trans_mode: '',
    trans_type: '',
    quotation_id: '',
    customer_id: '',
    customer_branch_id: '',
    job_owner_id: '',
    pol: '',
    pod: '',
    load_type: '',
    shipment_type: '',
    shipper: '',
    consignee_id: '',
    total_weight: '',
    weight_unit: 'Kg',
    incoterm: '',
    dimension_length: '',
    dimension_width: '',
    dimension_height: '',
    dimension_unit: 'cm',
    lcl_weight: '',
    lcl_weight_unit: 'Kg',
  };

  const formik = useFormik({
    initialValues: initialValues,
    onSubmit: (values) => {
      setDisableSubmit(true);
      try {
        const config = {
          headers: {
            'auth-token': token,
            'user_id': user_id,
          },
        };
        values.id = atob(b64id);
        axios
          .post(`${adminMainUrl}/api/jobOrder/update`, values, config)
          .then((response) => {
            if (response.status === 200) {
              toast.success(response?.data?.msg);
              setDisableSubmit(false);
              refresh();
              handleClose();
            } else {
              toast.error(response?.data?.msg);
              setDisableSubmit(false);
            }
          })
          .catch((error) => {
            console.log(error);
            toast.error(error.response?.data?.msg || error.message);
            setDisableSubmit(false);
          });
      } catch (error) {
        console.log(error.message);
        toast.error(error.message);
        setDisableSubmit(false);
      }
    },
  });

  // ---- Populate form from JobOrderDetail ----
  useEffect(() => {
    if (JobOrderDetail) {
      setIsDirectCustomer(JobOrderDetail.is_direct_customer === true);

      if (JobOrderDetail.customer_id) {
        dispatch(getCompanyBranch(token, user_id, JobOrderDetail.customer_id));
      }

      formik.setFieldValue('trans_mode', JobOrderDetail?.trans_mode || '');
      formik.setFieldValue('trans_type', JobOrderDetail?.trans_type || '');
      formik.setFieldValue('customer_branch_id', JobOrderDetail?.customer_branch_id || '');
      formik.setFieldValue('job_owner_id', JobOrderDetail?.job_owner_id ? `${JobOrderDetail.job_owner_id}` : '');
      formik.setFieldValue('pol', JobOrderDetail?.pol || '');
      formik.setFieldValue('pod', JobOrderDetail?.pod || '');
      formik.setFieldValue('load_type', JobOrderDetail?.load_type || '');
      formik.setFieldValue('shipment_type', JobOrderDetail?.shipment_type || '');
      formik.setFieldValue('shipper', JobOrderDetail?.shipper || '');
      formik.setFieldValue('consignee_id', JobOrderDetail?.consignee_id || '');
      formik.setFieldValue('total_weight', JobOrderDetail?.total_weight || '');
      formik.setFieldValue('weight_unit', JobOrderDetail?.weight_unit || 'Kg');
      // Incoterm: try incoterm then incoterm_id
      formik.setFieldValue('incoterm', JobOrderDetail?.incoterm || JobOrderDetail?.incoterm_id || '');
      formik.setFieldValue('dimension_length', JobOrderDetail?.dimension_length || '');
      formik.setFieldValue('dimension_width', JobOrderDetail?.dimension_width || '');
      formik.setFieldValue('dimension_height', JobOrderDetail?.dimension_height || '');
      formik.setFieldValue('dimension_unit', JobOrderDetail?.dimension_unit || 'cm');
      formik.setFieldValue('lcl_weight', JobOrderDetail?.lcl_weight || '');
      formik.setFieldValue('lcl_weight_unit', JobOrderDetail?.lcl_weight_unit || 'Kg');
    }
  }, [JobOrderDetail]);

  // ---- Helper for quotation branch change (if needed) ----
  const handleChangeCompanyBranch = (quotation_id) => {
    try {
      formik.setFieldValue('customer_id', '');
      formik.setFieldValue('customer_branch_id', '');
      const config = { headers: { 'auth-token': token, 'user_id': user_id } };
      axios
        .get(
          `${adminMainUrl}/api/jobOrder/get-companydetails-on-quotation?quotation_id=${quotation_id}`,
          config
        )
        .then((response) => {
          if (response.status === 200 && response.data?.data?.customer_data) {
            const cust = response.data.data.customer_data;
            formik.setFieldValue('customer_id', cust.id);
            setCompanyName(
              `${cust.name} ${cust.alias ? `(${cust.alias})` : ''} - ${cust.customerType}`
            );
            if (cust.customer_branch && Array.isArray(cust.customer_branch)) {
              const arr = cust.customer_branch.map((v) => ({
                value: v.id,
                label: v.branch_name,
              }));
              setBranchOption(arr);
            } else {
              setBranchOption([]);
            }
          } else {
            setCompanyName('');
            formik.setFieldValue('customer_id', '');
            setBranchOption([]);
          }
        })
        .catch((err) => console.log(err));
    } catch (error) {
      console.log(error.message);
    }
  };

  return (
    <>
      <Offcanvas show={show} onHide={handleClose} placement={'end'} name={'end'}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Edit Job Order</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <form onSubmit={formik.handleSubmit}>
            <div className="row">
              {/* ── Transport Mode ── */}
              <div className="col-md-6 mt-2">
                <div className="ms-2">
                  <label className="form-label mb-0">Transport Mode</label>
                  <div className="icon-forms mb-3 mb-sm-0">
                    <select
                      className="form-control form-control-sm"
                      name="trans_mode"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.trans_mode}
                    >
                      <option value="">Select Transport Mode</option>
                      <option value="Air">Air</option>
                      <option value="Sea">Sea</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Transport Type ── */}
              <div className="col-md-6 mt-2">
                <div className="ms-2">
                  <label className="form-label mb-0">Transport Type</label>
                  <div className="icon-forms mb-3 mb-sm-0">
                    <select
                      className="form-control form-control-sm"
                      name="trans_type"
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      value={formik.values.trans_type}
                    >
                      <option value="">Select Transport Type</option>
                      <option value="Import">Import</option>
                      <option value="Export">Export</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Branch – hidden when trans_mode === 'Sea' (matches Add) ── */}
              {formik.values.trans_mode !== 'Sea' && (
                <div className="col-md-6 mt-2">
                  <div className="ms-2">
                    <label className="form-label mb-0">Branch</label>
                    <div className="icon-forms mb-3 mb-sm-0">
                      <Select
                        className="custom-selector"
                        options={branchOption}
                        name="customer_branch_id"
                        onChange={(option) =>
                          formik.setFieldValue('customer_branch_id', option.value)
                        }
                        value={
                          branchOption.find(
                            (option) => option.value === formik.values.customer_branch_id
                          ) || null
                        }
                        isLoading={loadingJobOrderCompanyBranchList}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Job Owner – hidden when trans_mode === 'Sea' (matches Add) ── */}
              {formik.values.trans_mode !== 'Sea' && (
                <div className="col-md-6 mt-2">
                  <div className="ms-2">
                    <label className="form-label mb-0">Job Owner</label>
                    <div className="icon-forms mb-3 mb-sm-0">
                      <Select
                        className="custom-selector"
                        options={employeeOptions}
                        name="job_owner_id"
                        onChange={(option) =>
                          formik.setFieldValue('job_owner_id', option.value)
                        }
                        value={
                          employeeOptions.find(
                            (option) => option.value === formik.values.job_owner_id
                          ) || null
                        }
                        isLoading={loadingEmployee}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Sea specific fields ── */}
            {formik.values.trans_mode === 'Sea' && (
              <div className="row mt-3 border-top pt-3">
                {/* POL */}
                <div className="col-md-6 mt-2">
                  <div className="ms-2">
                    <label className="form-label mb-0">Port of Loading (POL)</label>
                    <div className="icon-forms mb-3 mb-sm-0">
                      <Select
                        className="custom-selector"
                        placeholder="Select Port of Loading"
                        options={portOptions}
                        styles={customStyles}
                        name="pol"
                        onChange={(option) =>
                          formik.setFieldValue('pol', option ? option.value : null)
                        }
                        value={
                          portOptions.find((o) => o.value === formik.values.pol) || null
                        }
                        isLoading={loadingJobOrderPortList}
                      />
                    </div>
                  </div>
                </div>

                {/* POD */}
                <div className="col-md-6 mt-2">
                  <div className="ms-2">
                    <label className="form-label mb-0">Port of Discharge (POD)</label>
                    <div className="icon-forms mb-3 mb-sm-0">
                      <Select
                        className="custom-selector"
                        placeholder="Select Port of Discharge"
                        options={portOptions}
                        styles={customStyles}
                        name="pod"
                        onChange={(option) =>
                          formik.setFieldValue('pod', option ? option.value : null)
                        }
                        value={
                          portOptions.find((o) => o.value === formik.values.pod) || null
                        }
                        isLoading={loadingJobOrderPortList}
                      />
                    </div>
                  </div>
                </div>

                {/* Load Type */}
                <div className="col-md-6 mt-2">
                  <div className="ms-2">
                    <label className="form-label mb-0">Load Type</label>
                    <div className="icon-forms mb-3 mb-sm-0">
                      <Select
                        className="custom-selector"
                        placeholder="Select Load Type"
                        options={loadTypeOptions}
                        styles={customStyles}
                        name="load_type"
                        onChange={(option) => {
                          formik.setFieldValue('load_type', option.value);
                          formik.setFieldValue('total_weight', '');
                          formik.setFieldValue('weight_unit', 'Kg');
                          formik.setFieldValue('dimension_length', '');
                          formik.setFieldValue('dimension_width', '');
                          formik.setFieldValue('dimension_height', '');
                          formik.setFieldValue('dimension_unit', 'cm');
                          formik.setFieldValue('lcl_weight', '');
                          formik.setFieldValue('lcl_weight_unit', 'Kg');
                          formik.setFieldValue('incoterm', '');
                        }}
                        value={
                          loadTypeOptions.find(
                            (o) => o.value === formik.values.load_type
                          ) || null
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Shipment Type */}
                <div className="col-md-6 mt-2">
                  <div className="ms-2">
                    <label className="form-label mb-0">Shipment Type</label>
                    <div className="icon-forms mb-3 mb-sm-0">
                      <Select
                        className="custom-selector"
                        placeholder="Select Shipment Type"
                        options={shipmentTypeOptions}
                        styles={customStyles}
                        name="shipment_type"
                        onChange={(option) =>
                          formik.setFieldValue('shipment_type', option.value)
                        }
                        value={
                          shipmentTypeOptions.find(
                            (o) => o.value === formik.values.shipment_type
                          ) || null
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* Consignee */}
                <div className="col-md-6 mt-2">
                  <div className="ms-2">
                    <label className="form-label mb-0">Consignee</label>
                    <div className="icon-forms mb-3 mb-sm-0">
                      <Select
                        className="custom-selector"
                        placeholder="Select Consignee"
                        options={consigneeOptions}
                        styles={customStyles}
                        name="consignee_id"
                        onChange={(option) =>
                          formik.setFieldValue('consignee_id', option ? option.value : null)
                        }
                        value={
                          consigneeOptions.find(
                            (o) => o.value === formik.values.consignee_id
                          ) || null
                        }
                        isLoading={loadingFFAirExpOrganisationList}
                        isClearable
                      />
                    </div>
                  </div>
                </div>

                {/* Shipper – shown ONLY when is_direct_customer === false (indirect) */}
                {isDirectCustomer === false && (
                  <div className="col-md-6 mt-2">
                    <div className="ms-2">
                      <label className="form-label mb-0">Shipper</label>
                      <div className="icon-forms mb-3 mb-sm-0">
                        <Select
                          className="custom-selector"
                          placeholder="Select Shipper"
                          options={shipperOptions}
                          styles={customStyles}
                          name="shipper"
                          onChange={(option) =>
                            formik.setFieldValue('shipper', option ? option.value : null)
                          }
                          value={
                            shipperOptions.find((o) => o.value === formik.values.shipper) ||
                            null
                          }
                          isLoading={loadingFFAirExpOrganisationList}
                          isClearable
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── FCL fields ── */}
                {formik.values.load_type === 'FCL' && (
                  <div className="row">
                    <div className="col-md-6 mt-2">
                      <div className="ms-2">
                        <label className="form-label mb-0">Total Weight *</label>
                        <div className="d-flex align-items-center gap-2">
                          <div style={{ flex: 2 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              name="total_weight"
                              placeholder="Enter weight"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.total_weight}
                            />
                          </div>
                          <div style={{ flex: 1, minWidth: '100px' }}>
                            <Select
                              className="custom-selector"
                              options={weightUnitOptions}
                              styles={customStyles}
                              name="weight_unit"
                              onChange={(option) =>
                                formik.setFieldValue('weight_unit', option.value)
                              }
                              value={
                                weightUnitOptions.find(
                                  (o) => o.value === formik.values.weight_unit
                                ) || null
                              }
                              placeholder="Unit"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mt-2">
                      <div className="ms-2">
                        <label className="form-label mb-0">Incoterm</label>
                        <div className="icon-forms mb-3 mb-sm-0">
                          <Select
                            className="custom-selector"
                            options={incotermOptions}
                            styles={customStyles}
                            name="incoterm"
                            onChange={(option) =>
                              formik.setFieldValue('incoterm', option ? option.value : null)
                            }
                            value={
                              incotermOptions.find(
                                (o) => o.value === formik.values.incoterm
                              ) || null
                            }
                            isLoading={loadingIncoterms}
                            isClearable
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── LCL fields ── */}
                {formik.values.load_type === 'LCL' && (
                  <div className="row">
                    {/* Dimension */}
                    <div className="col-md-6 mt-2">
                      <div className="ms-2">
                        <label className="form-label mb-1">Dimension (L × W × H)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              name="dimension_length"
                              placeholder="Length"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.dimension_length}
                            />
                          </div>
                          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>×</span>
                          <div style={{ flex: 1 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              name="dimension_width"
                              placeholder="Width"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.dimension_width}
                            />
                          </div>
                          <span style={{ fontSize: '18px', fontWeight: 'bold' }}>×</span>
                          <div style={{ flex: 1 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              name="dimension_height"
                              placeholder="Height"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.dimension_height}
                            />
                          </div>
                          <div style={{ flex: '0 0 100px', minWidth: '80px' }}>
                            {/* <label
                              className="form-label small mb-0"
                              style={{ fontWeight: '600', fontSize: '12px' }}
                            >
                              Unit
                            </label> */}
                            <Select
                              className="custom-selector"
                              options={dimensionUnitOptions}
                              styles={customStyles}
                              name="dimension_unit"
                              onChange={(option) =>
                                formik.setFieldValue('dimension_unit', option.value)
                              }
                              value={
                                dimensionUnitOptions.find(
                                  (o) => o.value === formik.values.dimension_unit
                                ) || null
                              }
                              placeholder="Select"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Weight (optional) */}
                    <div className="col-md-6 mt-2">
                      <div className="ms-2">
                        <label className="form-label mb-1">Weight (optional)</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ flex: 1 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              name="lcl_weight"
                              placeholder="Enter weight"
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              value={formik.values.lcl_weight}
                            />
                          </div>
                          <div style={{ flex: '0 0 100px', minWidth: '80px' }}>
                            {/* <label
                              className="form-label small mb-0"
                              style={{ fontWeight: '600', fontSize: '12px' }}
                            >
                              Unit
                            </label> */}
                            <Select
                              className="custom-selector"
                              options={weightUnitOptions}
                              styles={customStyles}
                              name="lcl_weight_unit"
                              onChange={(option) =>
                                formik.setFieldValue('lcl_weight_unit', option.value)
                              }
                              value={
                                weightUnitOptions.find(
                                  (o) => o.value === formik.values.lcl_weight_unit
                                ) || null
                              }
                              placeholder="Select"
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Incoterm */}
                    <div className="col-md-6 mt-2">
                      <div className="ms-2">
                        <label className="form-label mb-0">Incoterm</label>
                        <div className="icon-forms mb-3 mb-sm-0">
                          <Select
                            className="custom-selector"
                            options={incotermOptions}
                            styles={customStyles}
                            name="incoterm"
                            onChange={(option) =>
                              formik.setFieldValue('incoterm', option ? option.value : null)
                            }
                            value={
                              incotermOptions.find(
                                (o) => o.value === formik.values.incoterm
                              ) || null
                            }
                            isLoading={loadingIncoterms}
                            isClearable
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Submit Button ── */}
            <div className="row d-flex justify-content-end mt-3">
              <div className="col-md-6 d-flex justify-content-end">
                <button
                  type="submit"
                  className="btn btn-sm btn-danger"
                  disabled={disableSubmit}
                >
                  {disableSubmit ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i> Saving...
                    </>
                  ) : (
                    'Update'
                  )}
                </button>
              </div>
            </div>
          </form>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
};

export default JobOrderEditOffcanvas;